import { useState, useRef, useCallback } from 'react';
import { useScrubPause } from '../hooks/useScrubPause';

interface LessonVideoPreviewProps {
  /** Video URL or Firebase Storage path to display */
  videoUrl: string;
  /** Optional poster image */
  poster?: string;
  /** Title to display above the video */
  title?: string;
  /** Subtitle (e.g., coach/player name) */
  subtitle?: string;
  /** Max height for the video container */
  maxHeight?: string;
  /** Whether to show a compact version */
  compact?: boolean;
  /** Callback when video ends */
  onEnded?: () => void;
  /** Optional className for container */
  className?: string;
}

/**
 * A consistent video player component for displaying lesson videos
 * Used in both conversation view (MessageBubble) and order/lesson detail views
 */
export default function LessonVideoPreview({
  videoUrl,
  poster,
  title,
  subtitle,
  maxHeight = '374px',
  compact = false,
  onEnded,
  className = '',
}: LessonVideoPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { begin: beginScrub, end: endScrub } = useScrubPause(() => videoRef.current);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showControls, setShowControls] = useState(false);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      setIsLoading(false);
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, []);

  const handlePlay = useCallback(() => setIsPlaying(true), []);
  const handlePause = useCallback(() => setIsPlaying(false), []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    onEnded?.();
  }, [onEnded]);

  const handleError = useCallback(() => {
    setError('Failed to load video');
    setIsLoading(false);
  }, []);

  const togglePlay = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  }, [isPlaying]);

  const handleSeek = useCallback((time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (error) {
    return (
      <div className={`bg-gray-900 rounded-lg flex items-center justify-center ${className}`} style={{ height: compact ? '180px' : '234px' }}>
        <div className="text-center text-white p-4">
          <svg className="w-12 h-12 mx-auto mb-2 text-db-text-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-db-text-3">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg overflow-hidden bg-gray-900 ${className}`}>
      {/* Title section */}
      {(title || subtitle) && !compact && (
        <div className="bg-gray-800 px-4 py-2">
          {title && <h4 className="text-sm font-medium text-white">{title}</h4>}
          {subtitle && <p className="text-xs text-db-text-3">{subtitle}</p>}
        </div>
      )}

      {/* Video container */}
      <div
        className="relative bg-black w-full"
        style={{ height: maxHeight }}
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
      >
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
          </div>
        )}

        {/* Video element */}
        <video
          ref={videoRef}
          src={videoUrl}
          poster={poster}
          className="absolute inset-0 w-full h-full object-contain"
          style={{}}
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          onError={handleError}
          preload="metadata"
          playsInline
        />

        {/* Custom overlay controls */}
        <div
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
            showControls || !isPlaying ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Play/Pause button */}
          <button
            onClick={togglePlay}
            className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
        </div>

        {/* Bottom controls bar */}
        <div
          className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 transition-opacity duration-200 ${
            showControls || !isPlaying ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Progress bar */}
          <div className="mb-2">
            <input
              type="range"
              min={0}
              max={duration || 100}
              step={0.1}
              value={currentTime}
              onChange={(e) => handleSeek(parseFloat(e.target.value))}
              onPointerDown={beginScrub}
              onPointerUp={endScrub}
              onPointerCancel={endScrub}
              onKeyDown={beginScrub}
              onKeyUp={endScrub}
              onBlur={endScrub}
              className="w-full h-1 bg-white/30 rounded-lg appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-3
                [&::-webkit-slider-thumb]:h-3
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-white
                [&::-webkit-slider-thumb]:cursor-pointer
                [&::-moz-range-thumb]:w-3
                [&::-moz-range-thumb]:h-3
                [&::-moz-range-thumb]:rounded-full
                [&::-moz-range-thumb]:bg-white
                [&::-moz-range-thumb]:cursor-pointer
                [&::-moz-range-thumb]:border-0"
              style={{
                background: `linear-gradient(to right, #1A6B8A ${(currentTime / (duration || 1)) * 100}%, rgba(255,255,255,0.3) ${(currentTime / (duration || 1)) * 100}%)`,
              }}
            />
          </div>

          {/* Time display */}
          <div className="flex items-center justify-between text-white text-xs">
            <span>{formatTime(currentTime)} / {formatTime(duration)}</span>

            {/* Fullscreen button */}
            <button
              onClick={() => videoRef.current?.requestFullscreen()}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              aria-label="Fullscreen"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
